import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { TanTable, TabData } from './TanTable';
import { NotSentToGwiMoveOrder } from '../services/orders.service';
import ordersService from '../services/orders.service';
import dispatchersService from '../services/dispatchers.service';
import InfoModal from './common/InfoModal';
import { config } from '../config/config';
import { useDeadlineCheck } from '../hooks/useDeadlineCheck';
import ReasonNoteModal from './ReasonNoteModal';
import dispatchRemovalService from '../services/dispatchRemoval.service';
import { useViewedPros } from '../contexts/ViewedProsContext';

// Sentinel value for the "All Dispatchers" dropdown option. We can't use ''
// because '' is a valid (though unlikely) dispatcher username, and we can't
// use null because null is reserved for the "No User Found / unselected"
// state that forces the user to make a choice.
const ALL_DISPATCHERS = '__ALL__';

// The dispatcher pick is remembered per user in localStorage so it survives a
// refresh or navigating away and back. Reads/writes are guarded because
// storage can be unavailable (private windows, blocked site data).
const dispatcherStorageKey = (oid: string) => `notSentToGwiMove.dispatcher.${oid}`;
const readSavedDispatcher = (oid: string): string | null => {
  try { return localStorage.getItem(dispatcherStorageKey(oid)); } catch { return null; }
};
const writeSavedDispatcher = (oid: string | undefined, username: string) => {
  if (!oid) return;
  try { localStorage.setItem(dispatcherStorageKey(oid), username); } catch { /* ignore */ }
};

// ============================================================================
// TYPES
// ============================================================================

export interface NotSentToGwiMoveCardProps {
  /** Orders data */
  data: NotSentToGwiMoveOrder[];

  /** Eligible pending legs before dashboard filters. */
  totalCount?: number;

  /** Loading state */
  loading: boolean;

  /** Error message */
  error: string | null;

  /** Whether the card is collapsed */
  collapsed: boolean;

  /** Toggle collapse callback */
  onToggleCollapse: () => void;

  /** Whether in edit mode */
  isEditMode?: boolean;

  /** Whether the card is visible in edit mode */
  visible?: boolean;

  /** Toggle visibility callback */
  onToggleVisibility?: () => void;

  /** Card height */
  cardHeight?: 'sm' | 'md' | 'lg';

  /** Set card height callback */
  setCardHeight?: (height: 'sm' | 'md' | 'lg') => void;

  /** When true, card fills its parent container (for GridStack) instead of using fixed cardHeight */
  fillContainer?: boolean;

  /** Whether the card is locked (prevents GridStack drag/resize). When provided with onToggleCardLock, shows a lock icon in the toolbar. */
  cardLocked?: boolean;

  /** Toggle callback for the lock icon. */
  onToggleCardLock?: (locked: boolean) => void;

  /** Callback when PRO number is clicked to open OrderDetails */
  onProNumberClick?: (proNumber: number) => void;

  /** Current user's Azure AD object ID. Used to default the driver-dispatcher filter
   *  to whichever cms.dispatchers row is linked to this user via azure_user_id. */
  currentUserOid?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * NotSentToGwiMoveCard - Displays orders that have not been sent to gwiMove
 *
 * Features:
 * - TanTable with sortable columns
 * - Collapsible card with height control
 * - Search/filter functionality
 */
export const NotSentToGwiMoveCard: React.FC<NotSentToGwiMoveCardProps> = ({
  data,
  totalCount = data.length,
  loading,
  error,
  collapsed,
  onToggleCollapse,
  isEditMode = false,
  visible = true,
  onToggleVisibility,
  cardHeight = 'lg',
  setCardHeight,
  fillContainer = false,
  cardLocked,
  onToggleCardLock,
  onProNumberClick,
  currentUserOid,
}) => {
  // ============================================================================
  // STATE
  // ============================================================================

  const { isViewed, markViewed } = useViewedPros();
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const [activeTabs, setActiveTabs] = useState<string[]>(['today']);
  // Track the truly filtered rows from TanTable (after all filters: tabs, global search, column filters)
  const [visibleFilteredRows, setVisibleFilteredRows] = useState<NotSentToGwiMoveOrder[]>([]);
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRemoveConfirmDialog, setShowRemoveConfirmDialog] = useState(false);

  // Driver-dispatcher filter (persisted per user in localStorage, not user settings).
  //   null              -> not yet seeded — empty table until the options + oid arrive
  //   ALL_DISPATCHERS   -> show every row
  //   <username string> -> show only that dispatcher's rows
  // Seeded on first mount from the saved pick, else azure_user_id match, else All.
  const [selectedDispatcherUsername, setSelectedDispatcherUsername] = useState<string | null>(null);
  const dispatcherFilterInitialized = useRef(false);

  // Timer ref for success message auto-clear
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Deadline check hook for driver removal
  const { checkAndPrompt, showNoteModal, handleNoteSubmit: onNoteSubmit, handleNoteCancel, isChecking } = useDeadlineCheck();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  // ============================================================================
  // TAB DATA
  // ============================================================================

  // Helper function to format date from YYYY-MM-DD to MM/DD
  const formatShortDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}`; // MM/DD
    }
    return dateStr;
  };

  // Full set of driver-dispatchers — pulled from cms.dispatchers where
  // marked as driver-dispatchers OR assigned a current driver (including mules).
  // Dispatchers with no current rows are still listed; picking one yields an
  // empty table.
  const [dispatcherOptions, setDispatcherOptions] = useState<{ username: string; displayname: string; azure_user_id: string }[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    dispatchersService.list({ isDriverDispatcher: true, includeDriverRoster: true }, controller.signal)
      .then(rows => {
        setDispatcherOptions(
          rows
            .map(d => ({
              username: d.username,
              displayname: d.displayname || d.username,
              azure_user_id: d.azure_user_id || '',
            }))
            .sort((a, b) => a.displayname.localeCompare(b.displayname))
        );
      })
      .catch(err => {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        console.error('Failed to load driver dispatchers:', err);
      });
    return () => controller.abort();
  }, []);

  // Once we have the dispatcher list AND an oid to match against, seed the
  // filter in priority order:
  //   1. the pick this user saved last time (if it is still a listed option)
  //   2. the dispatcher row linked to the current user via azure_user_id
  //   3. All Dispatchers — non-dispatchers (managers, ops, admins) should see
  //      every pending leg rather than an empty "No User Found" table.
  // We intentionally wait for currentUserOid to be defined before flipping the
  // initialized flag, so we don't lock in a decision while the permissions
  // context is still loading the oid.
  useEffect(() => {
    if (dispatcherFilterInitialized.current) return;
    if (dispatcherOptions.length === 0) return;
    if (!currentUserOid) return;
    const saved = readSavedDispatcher(currentUserOid);
    const savedIsValid = saved === ALL_DISPATCHERS || dispatcherOptions.some(d => d.username === saved);
    const match = dispatcherOptions.find(d => d.azure_user_id === currentUserOid);
    setSelectedDispatcherUsername(savedIsValid && saved ? saved : (match?.username ?? ALL_DISPATCHERS));
    dispatcherFilterInitialized.current = true;
  }, [dispatcherOptions, currentUserOid]);

  // Tri-state filter:
  //   null               -> "No User Found / unselected" — show empty table
  //   ALL_DISPATCHERS    -> show every row
  //   <username string>  -> show only that dispatcher's rows
  const filteredData = useMemo(() => {
    if (selectedDispatcherUsername === null) return [];
    if (selectedDispatcherUsername === ALL_DISPATCHERS) return data;
    return data.filter(row => row.driverDispatcherUsername === selectedDispatcherUsername);
  }, [data, selectedDispatcherUsername]);

  // Get unique dates - stable reference that only changes when dates actually change
  const uniqueDatesStr = useMemo(() => {
    const uniqueDates = Array.from(
      new Set(filteredData.map(order => order.apptDate).filter(date => date))
    ).sort();
    return uniqueDates.join(',');
  }, [filteredData]);

  // Build lookup map from legrecno to row for finding linked legs
  const legrecnoToRow = useMemo(() => {
    const map = new Map<number, NotSentToGwiMoveOrder>();
    filteredData.forEach(row => {
      map.set(row.legrecno, row);
    });
    return map;
  }, [filteredData]);

  // Create tabs based on unique appointment dates
  const tabData = useMemo<TabData<NotSentToGwiMoveOrder>[]>(() => {
    // Get today's date in YYYY-MM-DD format
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    // Parse unique dates from the stable string
    const uniqueDates = uniqueDatesStr ? uniqueDatesStr.split(',') : [];

    // Separate dates into past due, today, and future
    const pastDueDates = uniqueDates.filter(date => date < todayStr);
    const futureDates = uniqueDates.filter(date => date > todayStr);

    // Create tabs array
    const tabs: TabData<NotSentToGwiMoveOrder>[] = [];

    // Add Past Due tab if there are any past dates
    if (pastDueDates.length > 0) {
      tabs.push({
        id: 'past-due',
        label: 'Past Due',
        filterFn: (row) => row.apptDate < todayStr,
      });
    }

    // Add Today tab
    tabs.push({
      id: 'today',
      label: 'Today',
      filterFn: (row) => row.apptDate === todayStr,
    });

    // Add a tab for each future date
    futureDates.forEach(date => {
      tabs.push({
        id: date,
        label: formatShortDate(date), // Display as MM/DD
        filterFn: (row) => row.apptDate === date,
      });
    });

    return tabs;
  }, [uniqueDatesStr]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  // Create unique key for each row
  const getRowKey = (row: NotSentToGwiMoveOrder) =>
    `${row.proNumber}-${row.legnum}-${row.unitNumber}`;

  // Get all linked rows for a given row (including the row itself)
  const getLinkedRows = useCallback((row: NotSentToGwiMoveOrder): NotSentToGwiMoveOrder[] => {
    const rows = [row];
    if (row.linkedLegRecnos && row.linkedLegRecnos.length > 0) {
      row.linkedLegRecnos.forEach(legrecno => {
        const linkedRow = legrecnoToRow.get(legrecno);
        if (linkedRow) {
          rows.push(linkedRow);
        }
      });
    }
    return rows;
  }, [legrecnoToRow]);

  // Callback to receive filtered rows from TanTable (after all filters: tabs, global search, column filters)
  const handleFilteredRowsChange = useCallback((filteredRows: NotSentToGwiMoveOrder[]) => {
    setVisibleFilteredRows(filteredRows);
  }, []);

  // When the user changes search input or column filters, clear selection so that
  // hidden rows can't be accidentally acted on after filtering.
  const handleUserFilterChange = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  // Check if all visible rows are selected (uses truly filtered rows from TanTable)
  const allSelected = visibleFilteredRows.length > 0 && visibleFilteredRows.every(row => selectedRows.has(getRowKey(row)));
  const someSelected = selectedRows.size > 0 && !allSelected;
  // TanTable reports rows after tabs/search/column filters, before pagination.
  const hiddenCount = Math.max(0, totalCount - visibleFilteredRows.length);

  // Toggle all visible checkboxes (only those currently visible after all filters)
  // Also handles linked legs - selecting/deselecting includes all linked legs
  const toggleAll = () => {
    const newSelected = new Set(selectedRows);
    if (allSelected) {
      // Deselect all visible rows and their linked legs
      visibleFilteredRows.forEach(row => {
        getLinkedRows(row).forEach(linkedRow => {
          newSelected.delete(getRowKey(linkedRow));
        });
      });
    } else {
      // Select all visible rows and their linked legs
      visibleFilteredRows.forEach(row => {
        getLinkedRows(row).forEach(linkedRow => {
          newSelected.add(getRowKey(linkedRow));
        });
      });
    }
    setSelectedRows(newSelected);
  };

  // Toggle individual checkbox - also toggles linked legs
  const toggleRow = (row: NotSentToGwiMoveOrder) => {
    const key = getRowKey(row);
    const newSelected = new Set(selectedRows);
    const isCurrentlySelected = newSelected.has(key);

    // Get all linked rows (including the row itself)
    const linkedRows = getLinkedRows(row);

    if (isCurrentlySelected) {
      // Deselect all linked rows
      linkedRows.forEach(linkedRow => {
        newSelected.delete(getRowKey(linkedRow));
      });
    } else {
      // Select all linked rows
      linkedRows.forEach(linkedRow => {
        newSelected.add(getRowKey(linkedRow));
      });
    }
    setSelectedRows(newSelected);
  };

  // Show confirmation dialog before sending
  const handleSendButtonClick = () => {
    if (selectedRows.size === 0) {
      setSendError('Please select at least one order to send');
      return;
    }
    setShowConfirmDialog(true);
  };

  // Send to gwiMove handler (called after confirmation)
  const handleConfirmSend = async () => {
    setShowConfirmDialog(false);
    setIsSending(true);
    setSendError(null);
    setSendSuccess(null);

    try {
      // Build items array from selected rows (only currently-visible rows can be acted on)
      const items = filteredData
        .filter(row => selectedRows.has(getRowKey(row)))
        .map(row => ({
          unitNumber: row.unitNumber,
          proNumber: row.proNumber,
          legrecno: row.legrecno,
          legnum: row.legnum,
        }));

      const response = await ordersService.sendToGwiMove(items);

      setSendSuccess(response.message);
      setSelectedRows(new Set()); // Clear selection after successful send

      // Show success for 5 seconds then clear (with cleanup)
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setSendSuccess(null), 5000);

    } catch (err: any) {
      setSendError(err.response?.data?.detail || err.message || 'Failed to send to gwiMove');
    } finally {
      setIsSending(false);
    }
  };

  // Show confirmation dialog before removing from driver
  const handleRemoveButtonClick = () => {
    if (selectedRows.size === 0) {
      setSendError('Please select at least one order to remove from driver');
      return;
    }
    setShowRemoveConfirmDialog(true);
  };

  // Remove from driver handler (called after confirmation)
  const handleConfirmRemove = async () => {
    setShowRemoveConfirmDialog(false);
    setIsRemoving(true);
    setSendError(null);
    setSendSuccess(null);

    try {
      // Build items array from selected rows (only currently-visible rows can be acted on)
      const selectedData = filteredData.filter(row => selectedRows.has(getRowKey(row)));
      const items = selectedData.map(row => ({
        proNumber: row.proNumber,
        legnum: row.legnum,
        origcode: row.origcode,
        destcode: row.destcode,
        origApptDate: row.origApptDate,
        destApptDate: row.destApptDate,
        driver: '',  // Empty string to remove driver
        oldDriver: row.driver || '',
      }));

      // Check deadline criteria before proceeding
      const deadlineCheck = await checkAndPrompt(
        selectedData.map(row => ({
          proNumber: row.proNumber,
          legnum: row.legnum,
          oldDriver: row.driver || '',
          newDriver: '',
        }))
      );

      if (deadlineCheck === null) {
        // User cancelled the note modal
        setIsRemoving(false);
        return;
      }

      // Attach enriched notes to qualifying pros: "Category - Reason - Notes" or "Category - Notes"
      if (deadlineCheck.note) {
        const enrichedNote = [deadlineCheck.category_name, deadlineCheck.reason_name, deadlineCheck.note].filter(Boolean).join(' - ');
        const qualifyingSet = new Set(deadlineCheck.qualifyingPros);
        items.forEach(item => {
          if (qualifyingSet.has(item.proNumber)) {
            (item as any).notes = enrichedNote;
          }
        });
      }

      const response = await ordersService.removeFromDriver(items);

      // Check if there were any failures
      if (response.failed > 0) {
        const errorMsg = response.errors?.length
          ? response.errors.join(', ')
          : `Failed to remove ${response.failed} move(s) from drivers`;
        setSendError(errorMsg);

        // Keep only failed items selected, deselect the ones that succeeded
        if (response.failedItems && response.failedItems.length > 0) {
          const failedSet = new Set(
            response.failedItems.map(fi => `${fi.proNumber}-${fi.legnum}`)
          );
          const failedKeys = new Set<string>();
          selectedData.forEach(row => {
            if (failedSet.has(`${row.proNumber}-${row.legnum}`)) {
              failedKeys.add(getRowKey(row));
            }
          });
          setSelectedRows(failedKeys);
        } else {
          // No structured failure data available, clear all to avoid stale state
          setSelectedRows(new Set());
        }
      } else {
        setSendSuccess(response.message);
        setSelectedRows(new Set()); // Clear selection after successful removal

        // Record dispatch removal audit entries
        if (deadlineCheck.note && deadlineCheck.qualifyingDetails.length > 0) {
          dispatchRemovalService.recordRemovals(
            deadlineCheck.qualifyingDetails.map(d => ({
              category_id: deadlineCheck.category_id!,
              reason_id: deadlineCheck.reason_id,
              notes: deadlineCheck.note!,
              pronumber: d.pronumber,
              driver: d.driver,
              dispatch_id: d.dispatch_id,
              appt_date: d.appt_date,
              deadline_type: d.deadline_type,
              deadline_date: d.deadline_date,
            }))
          ).catch(err => console.error('Failed to record dispatch removals:', err));
        }

        // Show success for 5 seconds then clear (with cleanup)
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => setSendSuccess(null), 5000);
      }

    } catch (err: any) {
      setSendError(err.response?.data?.detail || err.message || 'Failed to remove from driver');
    } finally {
      setIsRemoving(false);
    }
  };

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const columns = useMemo<ColumnDef<NotSentToGwiMoveOrder>[]>(
    () => [
      {
        id: 'select',
        header: 'Send',
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={selectedRows.has(getRowKey(row.original))}
              onChange={() => toggleRow(row.original)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
      },
      {
        accessorKey: 'proNumber',
        header: 'Pro',
        cell: (info) => {
          const proNumber = info.getValue() as number;
          const legacyCmsUrl = config.LEGACY_CMS_URL;
          const viewed = isViewed(proNumber);

          return (
            <div
              className={`flex items-center gap-2 px-1 rounded transition-colors duration-150 ${
                viewed ? 'bg-green-200 dark:bg-green-600/50' : ''
              }`}
              title={viewed ? 'Viewed' : undefined}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  markViewed(proNumber);
                  onProNumberClick?.(proNumber);
                }}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
              >
                {proNumber}
              </button>
              <a
                href={`${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${proNumber}&searchtype=pronumber`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  markViewed(proNumber);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Open in legacy CMS"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </a>
            </div>
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
      },
      {
        accessorKey: 'apptDate',
        header: 'Appt Date',
        cell: (info) => formatShortDate(info.getValue() as string),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
      },
      {
        accessorKey: 'customer',
        header: 'Customer',
        cell: (info) => info.getValue(),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
      },
      {
        accessorKey: 'origin',
        header: 'Origin',
        cell: (info) => info.getValue(),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
      },
      {
        accessorKey: 'destination',
        header: 'Destination',
        cell: (info) => info.getValue(),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
      },
      {
        accessorKey: 'driver',
        header: 'Driver',
        cell: (info) => info.getValue(),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
      },
      {
        accessorKey: 'driverDispatcher',
        header: 'Driver Dispatcher',
        cell: (info) => info.getValue(),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
      },
    ],
    [selectedRows, allSelected, someSelected, isViewed, markViewed, onProNumberClick]
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow ${fillContainer ? 'h-full flex flex-col overflow-hidden' : ''}`}>
      {error && (
        <div className="p-4 bg-red-50 text-red-600 rounded-lg">
          Error loading data: {error}
        </div>
      )}

      {sendError && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start">
          <span className="flex-1">{sendError}</span>
          <button
            onClick={() => setSendError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {sendSuccess && (
        <div className="mx-4 mt-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-start">
          <span className="flex-1">{sendSuccess}</span>
          <button
            onClick={() => setSendSuccess(null)}
            className="ml-2 text-green-500 hover:text-green-700"
          >
            ×
          </button>
        </div>
      )}

      <TanTable
        data={filteredData}
        columns={columns}
        loading={loading}
        settingsKey="not-sent-to-gwimove-card"
        fieldName="not-sent-to-gwimove-card"
        searchPlaceholder="Search orders..."
        cardTitle="Not Sent To gwiMove"
        cardLocked={cardLocked}
        onToggleCardLock={onToggleCardLock}
        cardHeight={cardHeight}
        onCardHeightChange={setCardHeight}
        fillContainer={fillContainer}
        enableGrouping={true}
        groupableColumns={['customer', 'origin', 'destination', 'driver', 'apptDate']}
        enableColumnFilters={true}
        enablePagination={true}
        paginationStyle="compactWithButtons"
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        showTabs={true}
        tabData={tabData}
        multiTabSelect={true}
        activeTabs={activeTabs}
        onActiveTabsChange={setActiveTabs}
        onFilteredRowsChange={handleFilteredRowsChange}
        onUserFilterChange={handleUserFilterChange}
        getRelatedRows={getLinkedRows}
        headerExtra={
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <select
              aria-label="Driver Dispatcher"
              title="Driver Dispatcher"
              value={selectedDispatcherUsername ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') return; // ignore re-picks of the sentinel
                setSelectedDispatcherUsername(val);
                writeSavedDispatcher(currentUserOid, val);
                setSelectedRows(new Set());
              }}
              className="px-2 py-0.5 text-sm border border-theme-border-secondary rounded bg-theme-bg-primary text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {selectedDispatcherUsername === null && (
                <option value="" disabled>No User Found — Select a Dispatcher</option>
              )}
              <option value={ALL_DISPATCHERS}>All Dispatchers</option>
              {dispatcherOptions.map(d => (
                <option key={d.username} value={d.username}>{d.displayname}</option>
              ))}
            </select>
            {!loading && !error && hiddenCount > 0 && (
              <span
                role="status"
                title="Pending legs hidden by dashboard filters, driver dispatcher, date tabs, search, or column filters."
                className="rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
              >
                {hiddenCount} hidden by filters
              </span>
            )}
          </div>
        }
        footerLeftContent={
          <>
            <button
              onClick={toggleAll}
              className="px-2 py-1 text-sm rounded font-medium transition-colors bg-gray-200 dark:bg-gray-700 text-label hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
            <button
              onClick={handleSendButtonClick}
              disabled={selectedRows.size === 0 || isSending || isRemoving}
              className={`px-2 py-1 text-sm rounded font-medium transition-colors ${
                selectedRows.size === 0 || isSending || isRemoving
                  ? 'bg-gray-300 dark:bg-gray-600 text-muted cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isSending ? 'Sending...' : 'Send to gwiMove'}
            </button>
            <button
              onClick={handleRemoveButtonClick}
              disabled={selectedRows.size === 0 || isSending || isRemoving}
              className={`px-2 py-1 text-sm rounded font-medium transition-colors ${
                selectedRows.size === 0 || isSending || isRemoving
                  ? 'bg-gray-300 dark:bg-gray-600 text-muted cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {isRemoving ? 'Removing...' : 'Remove From Driver'}
            </button>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {selectedRows.size > 0 && (
                <span>{selectedRows.size} leg{selectedRows.size !== 1 ? 's' : ''} selected</span>
              )}
            </div>
          </>
        }
      />

      {/* Send to gwiMove Confirmation Dialog */}
      <InfoModal
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title="Confirm Send to gwiMove"
        body={
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to send <span className="font-semibold">{selectedRows.size}</span> move{selectedRows.size !== 1 ? 's' : ''} to gwiMove?
          </p>
        }
        buttons={[
          {
            label: 'Cancel',
            onClick: () => setShowConfirmDialog(false),
            variant: 'secondary'
          },
          {
            label: 'Send',
            onClick: handleConfirmSend,
            variant: 'primary'
          }
        ]}
      />

      {/* Remove From Driver Confirmation Dialog */}
      <InfoModal
        isOpen={showRemoveConfirmDialog}
        onClose={() => setShowRemoveConfirmDialog(false)}
        title="Confirm Remove From Driver"
        body={
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to remove <span className="font-semibold">{selectedRows.size}</span> move{selectedRows.size !== 1 ? 's' : ''} from their assigned drivers?
          </p>
        }
        buttons={[
          {
            label: 'Cancel',
            onClick: () => setShowRemoveConfirmDialog(false),
            variant: 'secondary'
          },
          {
            label: 'Remove',
            onClick: handleConfirmRemove,
            variant: 'danger'
          }
        ]}
      />

      {/* Deadline Reason Note Modal */}
      {showNoteModal && (
        <ReasonNoteModal
          onClose={handleNoteCancel}
          onSubmit={onNoteSubmit}
        />
      )}
    </div>
  );
};
